
import Vue from "vue";
import { Component, Prop, Watch } from "vue-property-decorator";
import { inputTextValidationRules } from "@/util/input-text-validation-rules";
import {
  textAndRichTextVariableItem,
  textAndRichTextVariableOptions
} from "@/types/controls";
import Popout from "@/components/UI/Popout.vue";
import Icon from "@/components/UI/Icon.vue";
import Dropdown from "@/components/Reusable/Dropdown/Dropdown.vue";
import IconButton from "@/components/UI/IconButton.vue";
import VariablesDropdown from "@/components/Reusable/CustomInputs/VariablesDropdown.vue";
import { nl2br } from "@/util/nl2br";

@Component({
  components: { VariablesDropdown, IconButton, Dropdown, Popout, Icon }
})
export default class TextInput extends Vue {
  $refs!: {
    input: HTMLInputElement | HTMLTextAreaElement;
  };

  @Prop()
  value?: string;

  @Prop({ default: false })
  multiline!: boolean;

  @Prop()
  placeholder?: string;

  @Prop()
  autofocus?: boolean;

  @Prop()
  validationFunctionName!: keyof typeof inputTextValidationRules;

  @Prop({ default: "base" })
  size?: "md" | "base";

  @Prop({ default: "" })
  label?: string;

  @Prop()
  disabled?: boolean;

  @Prop()
  loading?: boolean;

  @Prop()
  variableOptions!: textAndRichTextVariableOptions;

  @Prop({ default: () => [] })
  variableItems!: textAndRichTextVariableItem[];

  // Necessary in order to validate the input
  internalValue?: string = "";

  variableDialogOpen = false;

  get displayValueWithBrsAsNewLines() {
    return (
      this.internalValue
        // match any <br> with or without spaces between brackets
        ?.replace(/<(br\s+)?(br)?>/gi, "\n")
    );
  }

  @Watch("value", { immediate: true })
  updateInternalValue() {
    this.internalValue = this.value;

    this.autogrowTextarea();
  }

  mounted() {
    // The HTML attribute "autofocus" only works once per component. By using `.focus()` it works all the time.
    if (this.autofocus) {
      this.$refs.input.focus();
    }

    this.autogrowTextarea();
  }

  edit(newVal: string) {
    this.internalValue = newVal;

    if (this.textInputRuleValidation === true) {
      this.$emit("input", newVal ? nl2br(newVal) : null);
    }
  }

  get textInputRuleValidation() {
    if (this.validationFunctionName) {
      return inputTextValidationRules[this.validationFunctionName](
        this.internalValue
      );
    }

    return true;
  }

  get cssClasses() {
    return [
      "breakdance-white-gray-outlined-input",
      "breakdance-simple-input",
      `breakdance-simple-input--${this.multiline ? "textarea" : "text"}`,
      `breakdance-simple-input--${this.size}`,
      { "breakdance-text-input--with-variables": this.variableOptions?.enabled }
    ];
  }

  insertVariable(text: string) {
    const cursorPosition = this.$refs.input.selectionStart;
    const variableText = `{${text}}`;
    let value = "";

    if (cursorPosition && this.internalValue) {
      const beforeCursorValue = this.internalValue.slice(0, cursorPosition);
      const afterCursorValue = this.internalValue.slice(cursorPosition);
      value = `${beforeCursorValue}${variableText}${afterCursorValue}`;
    } else {
      value = `${this.internalValue ?? ""}${variableText}`;
    }

    this.$emit("input", value);
  }

  autogrowTextarea() {
    const textarea = this.$refs.input;
    if (!this.multiline || !textarea) return;

    this.$nextTick(() => {
      textarea.style.height = "0px";
      textarea.style.height = Math.min(500, textarea.scrollHeight) - 4 + "px";
    });
  }
}
